import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  MenuItem,
  Radio,
  Select,
  Typography,
} from '@mui/material';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import LineTypeBadge from 'src/components/LineTypeBadge';
import CpNumberIndicator from 'src/components/CpNumberIndicator';
import { isPhoneNonTextable } from 'src/views/management/ChatView/contactPhones';
import helpers from 'src/helpers';

/**
 * PhoneSelector - Dropdown for selecting which phone to text
 *
 * Shows warning for non-textable phones (landlines) with tooltip explaining why.
 * Non-textable phones can still be selected (user may know better) but are highlighted.
 *
 * CON-1705 — the line type, the carrier and the blocked-region country now show
 * here, not just on the contact's own screen. Carrier is the reason a consultant
 * opens this at all as often as line type is: carrier-level filtering of
 * consultant traffic is rising, so "which of her two numbers is the Verizon one"
 * is a question this dropdown should answer without a trip to the contact record.
 *
 * ONE SIGNAL PER NUMBER. The icon says whether the number can receive texts; the
 * badge says why not. What used to be a red warning triangle beside a red icon
 * beside red text is now a red chip that names the reason in words, so the
 * triangle and its wrapping tooltip were removed rather than stacked with it.
 * Note the consequence, which was accepted deliberately: the hover target for a
 * non-textable row shrinks from the whole row to the chip. The chip carries the
 * explanation in text either way, where the triangle only asserted there was one.
 */
function PhoneSelector({
  isToolbarSelector = false,
  onChange,
  options,
  phoneInfoList = [],
  value = '',
  ...props
}) {
  const allContactPhonesValue = '';
  const phoneNumbers = useMemo(() => (
    isToolbarSelector ? [allContactPhonesValue, ...options] : options
  ), [isToolbarSelector, options]);

  // Get phone info for a specific phone number
  const getPhoneInfo = (phone) => {
    const index = options.indexOf(phone);
    return index >= 0 ? phoneInfoList[index] : null;
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      displayEmpty
      size="small"
      color="secondary"
      renderValue={(selected) => {
        const phoneInfo = getPhoneInfo(selected);
        const isNonTextable = isPhoneNonTextable(selected, phoneInfo);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: '#545454' }}>
            {!helpers.isEmpty(selected) && (
              isNonTextable
                ? <PhoneDisabledIcon fontSize="inherit" sx={{ color: '#F44336' }} />
                : <LocalPhoneOutlinedIcon fontSize="inherit" />
            )}
            <Typography variant="caption">
              {helpers.isEmpty(selected)
                ? 'All contact phones'
                : helpers.transform.toPhoneNumber(selected)}
            </Typography>
            {/*
              CON-1705 — guard is on the PHONE, not on lineType, matching the
              PhoneDetailsRow fix in CON-1704. A blocked-region number has
              line_type null, so a lineType guard would skip the badge on
              exactly the numbers that most need one. The badge returns null on
              its own when there is neither a line type nor a blocked region.
            */}
            {/* CON-1896 — a CP number shows the CP dot instead of the badge */}
            {!helpers.isEmpty(selected) && (phoneInfo?.isCpNumber === true ? (
              <CpNumberIndicator />
            ) : (
              <LineTypeBadge
                phoneNumber={selected}
                lineType={phoneInfo?.lineType}
                displayLineType={phoneInfo?.displayLineType}
                isTextable={phoneInfo?.isTextable}
                carrier={phoneInfo?.carrier}
                checkedAtRelative={phoneInfo?.checkedAtRelative}
              />
            ))}
          </Box>
        );
      }}
      MenuProps={{
        PaperProps: {
          style: {
            padding: '0 5px',
            borderRadius: '12px',
          },
        },
      }}
      sx={{
        '& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '14px',
        },
        '& .MuiSelect-select': {
          paddingY: '4px',
          fontSize: '14px',
        },

      }}
      {...props}
    >
      {phoneNumbers.map((phone) => {
        const phoneInfo = getPhoneInfo(phone);
        const isNonTextable = isPhoneNonTextable(phone, phoneInfo);

        return (
          <MenuItem
            key={phone}
            value={phone}
            sx={{
              color: isNonTextable ? '#F44336' : '#545454',
              borderRadius: '4px',
              padding: '0',
              marginBottom: '8px',
              backgroundColor: isNonTextable ? '#FFEBEE' : 'transparent',
              '&:hover': {
                backgroundColor: isNonTextable ? '#FFCDD2' : undefined,
              },
            }}
          >
            <Radio
              checked={value === phone}
              size="small"
              sx={{ padding: '4px' }}
            />
            {!helpers.isEmpty(phone) && (
              isNonTextable
                ? <PhoneDisabledIcon fontSize="inherit" sx={{ color: '#F44336' }} />
                : <LocalPhoneOutlinedIcon fontSize="inherit" />
            )}
            <Box sx={{ flex: 1, minWidth: 0, paddingY: '2px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="caption" alignContent="center">
                  {helpers.isEmpty(phone)
                    ? 'All contact phones'
                    : helpers.transform.toPhoneNumber(phone)}
                </Typography>
                {!helpers.isEmpty(phone) && (phoneInfo?.isCpNumber === true ? (
                  <CpNumberIndicator />
                ) : (
                  <LineTypeBadge
                    phoneNumber={phone}
                    lineType={phoneInfo?.lineType}
                    displayLineType={phoneInfo?.displayLineType}
                    isTextable={phoneInfo?.isTextable}
                    carrier={phoneInfo?.carrier}
                    checkedAtRelative={phoneInfo?.checkedAtRelative}
                  />
                ))}
              </Box>
              {/*
                CON-1705 — the carrier gets a line of its own here rather than
                living behind the badge tooltip. The mobile app hides it behind
                a long-press because a phone has neither hover nor room; the web
                list has both, and the badge is already spending its own label on
                the line type. A number with no lookup row simply has no caption.
              */}
              {!helpers.isEmpty(phoneInfo?.carrier) && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'inherit',
                    opacity: 0.75,
                    fontSize: '11px',
                    lineHeight: 1.3,
                  }}
                >
                  {phoneInfo.carrier}
                </Typography>
              )}
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
}

PhoneSelector.propTypes = {
  isToolbarSelector: PropTypes.bool,
  value: PropTypes.string,
  options: PropTypes.array.isRequired,
  phoneInfoList: PropTypes.arrayOf(PropTypes.shape({
    lineType: PropTypes.string,
    displayLineType: PropTypes.string,
    isTextable: PropTypes.bool,
    carrier: PropTypes.string,
    checkedAtRelative: PropTypes.string,
    isCpNumber: PropTypes.bool,
  })),
  onChange: PropTypes.func.isRequired,
};

export default PhoneSelector;
